import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  ArrowLeft, 
  CreditCard, 
  Calendar, 
  Loader2,
  Shield,
  Zap,
  MapPin
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const PAYPAL_CLIENT_ID = 'AQN6CD7Dlqw6cYWgrgCJ6qP-33JD7lKEWI1NZ9O1yIyDEBf1EQcbgiEH4W1_Y214Yd0mX8ZxnBM8cV3O';

// Canadian Provinces
const PROVINCES = [
  { code: 'AB', name: 'Alberta', rate: '5%', type: 'GST' },
  { code: 'BC', name: 'British Columbia', rate: '5%', type: 'GST' },
  { code: 'MB', name: 'Manitoba', rate: '5%', type: 'GST' },
  { code: 'NB', name: 'New Brunswick', rate: '15%', type: 'HST' },
  { code: 'NL', name: 'Newfoundland and Labrador', rate: '15%', type: 'HST' },
  { code: 'NS', name: 'Nova Scotia', rate: '15%', type: 'HST' },
  { code: 'NT', name: 'Northwest Territories', rate: '5%', type: 'GST' },
  { code: 'NU', name: 'Nunavut', rate: '5%', type: 'GST' },
  { code: 'ON', name: 'Ontario', rate: '13%', type: 'HST' },
  { code: 'PE', name: 'Prince Edward Island', rate: '15%', type: 'HST' },
  { code: 'QC', name: 'Quebec', rate: '14.975%', type: 'GST+QST' },
  { code: 'SK', name: 'Saskatchewan', rate: '5%', type: 'GST' },
  { code: 'YT', name: 'Yukon', rate: '5%', type: 'GST' },
];

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [selectedProvince, setSelectedProvince] = useState('ON');
  const [taxBreakdown, setTaxBreakdown] = useState(null);
  const [calculatingTax, setCalculatingTax] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState(null);
  const [couponApplied, setCouponApplied] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);

  useEffect(() => {
    if (location.state?.package) {
      const pkg = location.state.package;
      setPackageData(pkg);
      
      // Get coupon info from package state
      if (pkg.couponCode) {
        setCouponCode(pkg.couponCode);
      }
      if (pkg.couponApplied) {
        setCouponApplied(pkg.couponApplied);
        setDiscountAmount(pkg.couponApplied.discount_amount || 0);
      }
    } else {
      const status = searchParams.get('status');
      if (status === 'success') {
        setPaymentStatus('success');
      } else if (status === 'cancel') {
        setPaymentStatus('cancelled');
      }
    }
  }, [location.state, searchParams]);

  // Calculate tax when province or package changes
  useEffect(() => {
    if (packageData && selectedProvince) {
      calculateTax();
    }
  }, [packageData, selectedProvince, discountAmount]);

  const calculateTax = async () => {
    if (!packageData) return;
    
    // Calculate discounted price
    const originalPrice = packageData.price || 0;
    const priceAfterDiscount = Math.max(0, originalPrice - discountAmount);
    
    setCalculatingTax(true);
    try {
      const response = await fetch(`${API_BASE}/api/tax/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: priceAfterDiscount,
          province: selectedProvince
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTaxBreakdown(data);
      } else {
        // Fallback calculation
        const province = PROVINCES.find(p => p.code === selectedProvince);
        const rate = parseFloat(province?.rate) / 100 || 0.13;
        setTaxBreakdown({
          subtotal: priceAfterDiscount,
          total_tax_amount: priceAfterDiscount * rate,
          total_with_tax: priceAfterDiscount * (1 + rate),
          tax_type: province?.type || 'HST',
          province_name: province?.name || 'Ontario'
        });
      }
    } catch (error) {
      console.error('Error calculating tax:', error);
    } finally {
      setCalculatingTax(false);
    }
  };

  // PayPal Functions
  const createOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageData.package_id || packageData._id || packageData.id,
          package_type: packageData.type === 'boosted' ? 'boost_membership' : 'listing_credit',
          province: selectedProvince,
          coupon_code: couponApplied ? couponCode : null
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create order');
      }

      const approvalUrl = data.approval_url;
      const urlParams = new URLSearchParams(approvalUrl.split('?')[1]);
      const token_param = urlParams.get('token');
      
      return token_param || data.payment_id;
    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/payments/execute?payment_id=${data.paymentID || data.orderID}&payer_id=${data.payerID}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Payment execution failed');
      }

      setPaymentStatus('success');
      toast({
        title: "Payment Successful! 🎉",
        description: "Your package has been activated.",
      });
    } catch (error) {
      console.error('Error executing payment:', error);
      setPaymentStatus('error');
      toast({
        title: "Payment Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    setPaymentStatus('error');
    toast({
      title: "Payment Error",
      description: "Something went wrong with PayPal. Please try again.",
      variant: "destructive"
    });
  };

  const onCancel = () => {
    setPaymentStatus('cancelled');
    toast({
      title: "Payment Cancelled",
      description: "You cancelled the payment.",
    });
  };

  // Stripe Function
  const handleStripePayment = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast({
          title: "Login Required",
          description: "Please login to make a purchase",
          variant: "destructive"
        });
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: packageData.package_id || packageData._id || packageData.id,
          package_type: packageData.type === 'boosted' ? 'boost_membership' : 'listing_credit',
          origin_url: window.location.origin,
          province: selectedProvince,
          coupon_code: couponApplied ? couponCode : null
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  // Status Screens
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container max-w-lg mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Successful!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Your package has been activated successfully.</p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/trainer/dashboard')} className="w-full bg-red-600 hover:bg-red-700">Go to Dashboard</Button>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">Back to Home</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (paymentStatus === 'error' || paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container max-w-lg mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <div className={`w-20 h-20 ${paymentStatus === 'error' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <XCircle className={`w-12 h-12 ${paymentStatus === 'error' ? 'text-red-600' : 'text-yellow-600'}`} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {paymentStatus === 'error' ? 'Payment Failed' : 'Payment Cancelled'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {paymentStatus === 'error' ? 'Something went wrong. Please try again.' : 'No charges were made.'}
              </p>
              <div className="space-y-3">
                <Button onClick={() => setPaymentStatus(null)} className="w-full bg-red-600 hover:bg-red-700">Try Again</Button>
                <Button variant="outline" onClick={() => navigate('/trainer/dashboard')} className="w-full">Back to Dashboard</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container max-w-lg mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No Package Selected</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Please select a package from the dashboard.</p>
              <Button onClick={() => navigate('/trainer/dashboard')} className="bg-red-600 hover:bg-red-700">
                <ArrowLeft className="w-4 h-4 mr-2" />Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const isBoostPackage = packageData.type === 'boosted';
  const originalPrice = packageData.originalPrice || packageData.price || 0;
  const priceAfterDiscount = Math.max(0, originalPrice - discountAmount);
  const totalAmount = taxBreakdown?.total_with_tax || priceAfterDiscount;
  const taxAmount = taxBreakdown?.total_tax_amount || 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ResponsiveHeader />
      
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isBoostPackage ? <Zap className="w-5 h-5 text-yellow-500" /> : <CreditCard className="w-5 h-5 text-red-500" />}
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Package Info */}
              <div className={`p-4 rounded-lg ${isBoostPackage ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-800'}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{packageData.name}</h3>
                    <Badge variant={isBoostPackage ? "secondary" : "default"} className="mt-1">
                      {isBoostPackage ? 'Boost Membership' : 'Listing Credit'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>{packageData.duration_days} days</span>
                </div>
              </div>

              {/* Province Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <MapPin className="w-4 h-4" />
                  Your Province (for tax calculation)
                </label>
                <select
                  value={selectedProvince}
                  onChange={(e) => setSelectedProvince(e.target.value)}
                  className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                >
                  {PROVINCES.map(province => (
                    <option key={province.code} value={province.code}>
                      {province.name} ({province.type} {province.rate})
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Original Price</span>
                  <span className={`dark:text-white ${discountAmount > 0 ? 'line-through text-gray-400' : ''}`}>
                    ${originalPrice.toFixed(2)} CAD
                  </span>
                </div>
                
                {/* Coupon Discount */}
                {discountAmount > 0 && couponApplied && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Coupon: {couponCode}
                      {couponApplied.discount_type === 'percentage' && ` (${couponApplied.discount_value}%)`}
                    </span>
                    <span className="text-green-600 dark:text-green-400">-${discountAmount.toFixed(2)} CAD</span>
                  </div>
                )}
                
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal after discount</span>
                    <span className="font-medium dark:text-white">${priceAfterDiscount.toFixed(2)} CAD</span>
                  </div>
                )}
                
                {calculatingTax ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Tax</span>
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : taxBreakdown && (
                  <>
                    {taxBreakdown.hst_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">HST ({(taxBreakdown.hst_rate * 100).toFixed(0)}%)</span>
                        <span className="dark:text-white">${taxBreakdown.hst_amount.toFixed(2)} CAD</span>
                      </div>
                    )}
                    {taxBreakdown.gst_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">GST ({(taxBreakdown.gst_rate * 100).toFixed(0)}%)</span>
                        <span className="dark:text-white">${taxBreakdown.gst_amount.toFixed(2)} CAD</span>
                      </div>
                    )}
                    {taxBreakdown.qst_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">QST ({(taxBreakdown.qst_rate * 100).toFixed(3)}%)</span>
                        <span className="dark:text-white">${taxBreakdown.qst_amount.toFixed(2)} CAD</span>
                      </div>
                    )}
                  </>
                )}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="dark:text-white">Total</span>
                    <span className="text-red-600">${totalAmount.toFixed(2)} CAD</span>
                  </div>
                  {discountAmount > 0 && (
                    <p className="text-xs text-green-600 dark:text-green-400 text-right mt-1">
                      You save ${discountAmount.toFixed(2)} with coupon!
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-white">What you get:</h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  {isBoostPackage ? (
                    <>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Priority listing on homepage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Featured badge on your profile</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Increased visibility in search</li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Appear on homepage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Receive booking requests</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Full profile visibility</li>
                    </>
                  )}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Payment Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-500" />Secure Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-600 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Processing payment...</p>
                </div>
              ) : (
                <>
                  {/* Payment Method Selection */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Payment Method:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-4 rounded-lg border-2 transition-all ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                      >
                        <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-6 mx-auto" />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">PayPal</p>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('stripe')}
                        className={`p-4 rounded-lg border-2 transition-all ${paymentMethod === 'stripe' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <CreditCard className="w-6 h-6 text-purple-600" />
                          <span className="font-semibold text-purple-600">Stripe</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Credit/Debit Card</p>
                      </button>
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    {paymentMethod === 'paypal' ? (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">Pay securely with PayPal</p>
                        <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'CAD' }}>
                          <PayPalButtons
                            style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={onError}
                            onCancel={onCancel}
                          />
                        </PayPalScriptProvider>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">Pay securely with your card via Stripe</p>
                        <Button onClick={handleStripePayment} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6" disabled={loading}>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Pay ${totalAmount.toFixed(2)} CAD with Card
                        </Button>
                        <div className="flex items-center justify-center gap-3 mt-3">
                          <svg className="h-8" viewBox="0 0 50 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="50" height="16" rx="2" fill="#1A1F71"/>
                            <path d="M18.5 12.5L21 3.5H23.5L21 12.5H18.5Z" fill="white"/>
                            <path d="M29.5 3.7C29 3.5 28.2 3.3 27.2 3.3C24.5 3.3 22.6 4.7 22.6 6.7C22.6 8.2 24 9 25 9.5C26 10 26.4 10.3 26.4 10.8C26.4 11.5 25.5 11.8 24.7 11.8C23.6 11.8 23 11.6 22 11.2L21.6 11L21.2 13.5C22 13.8 23.3 14.1 24.7 14.1C27.6 14.1 29.4 12.7 29.4 10.6C29.4 9.4 28.7 8.5 27.2 7.8C26.3 7.3 25.7 7 25.7 6.5C25.7 6 26.3 5.5 27.4 5.5C28.3 5.5 29 5.7 29.5 5.9L29.8 6L30.2 3.7H29.5Z" fill="white"/>
                            <path d="M34.5 3.5C33.9 3.5 33.4 3.7 33.1 4.3L29 12.5H32L32.5 11H36L36.3 12.5H39L36.7 3.5H34.5ZM33.3 9C33.6 8.2 34.7 5.5 34.7 5.5C34.7 5.5 35 4.7 35.2 4.2L35.4 5.4C35.4 5.4 36 8.2 36.1 9H33.3Z" fill="white"/>
                            <path d="M16 3.5L13.3 9.8L13 8.3C12.5 6.6 11 4.8 9.3 3.9L11.8 12.5H14.8L19 3.5H16Z" fill="white"/>
                            <path d="M11 3.5H6.5L6.5 3.7C10 4.5 12.3 6.5 13 8.3L12.2 4.3C12.1 3.7 11.6 3.5 11 3.5Z" fill="#F9A533"/>
                          </svg>
                          <svg className="h-8" viewBox="0 0 50 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="50" height="30" rx="4" fill="#F5F5F5"/>
                            <circle cx="20" cy="15" r="9" fill="#EB001B"/>
                            <circle cx="30" cy="15" r="9" fill="#F79E1B"/>
                            <path d="M25 8.5C27 10 28.3 12.3 28.3 15C28.3 17.7 27 20 25 21.5C23 20 21.7 17.7 21.7 15C21.7 12.3 23 10 25 8.5Z" fill="#FF5F00"/>
                          </svg>
                          <svg className="h-8" viewBox="0 0 50 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="50" height="30" rx="4" fill="#016FD0"/>
                            <path d="M25 7L22 23H28L31 7H25Z" fill="white"/>
                            <path d="M15 7L10 23H16L18.5 10.5L20 23H25L22 7H15Z" fill="white"/>
                            <path d="M35 7L33 15L31 7H25L29 23H35L41 7H35Z" fill="white"/>
                          </svg>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Shield className="w-4 h-4" />
                    <span>Your payment information is secure and encrypted</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PaymentPage;
